/**
 * Prescription-templates mock adapter.
 *
 * Mirrors `prescriptionTemplatesApi` in `../prescriptionTemplates.ts` so the
 * `MOCKS_ENABLED ? mock : real` swap is a no-op for callers. Seeds common VN
 * outpatient templates so `/prescriptions/templates` (+ edit) and the
 * encounter template picker are click-through with no backend/tenant header.
 */
import { delay } from "./registry";
import type { PageResponse } from "../../types/patient";
import type {
  PrescriptionTemplateItemResponse,
  PrescriptionTemplateListFilter,
  PrescriptionTemplateResponse,
  PrescriptionTemplateSaveRequest,
  PrescriptionTemplateSummaryResponse,
} from "../../types/prescriptionTemplate";

const nowISO = new Date().toISOString();

function item(
  i: number,
  medicineId: string,
  medicineName: string,
  unit: string,
  qty: number,
  m: number,
  n: number,
  a: number,
  e: number
): PrescriptionTemplateItemResponse {
  return {
    id: `pti-${medicineId}-${i}`,
    medicineId,
    medicineName,
    medicineUnit: unit,
    medicineStrength: null,
    quantity: qty,
    doseMorning: m,
    doseNoon: n,
    doseAfternoon: a,
    doseEvening: e,
    doseUnit: unit,
    route: "PO",
    routeLabel: "Uống",
    mealTiming: "AFTER_MEAL",
    note: null,
    itemOrder: i,
  };
}

const TEMPLATES: PrescriptionTemplateResponse[] = [
  {
    id: "tpl-0001",
    code: "TPL-VIEM-HONG",
    name: "Viêm họng cấp",
    diagnosis: "Viêm họng cấp",
    icd10Code: "J02",
    durationDays: 5,
    notes: "Uống nhiều nước, nghỉ ngơi.",
    visible: true,
    active: true,
    createdAt: nowISO,
    updatedAt: nowISO,
    version: 1,
    items: [
      item(0, "med-0002", "Augmentin 625mg", "Viên", 14, 1, 0, 0, 1),
      item(1, "med-0003", "Paracetamol 500mg", "Viên", 15, 1, 1, 0, 1),
    ],
  },
  {
    id: "tpl-0002",
    code: "TPL-TANG-HA",
    name: "Tăng huyết áp – duy trì",
    diagnosis: "Tăng huyết áp vô căn",
    icd10Code: "I10",
    durationDays: 30,
    notes: "Theo dõi huyết áp hằng ngày.",
    visible: true,
    active: true,
    createdAt: nowISO,
    updatedAt: nowISO,
    version: 1,
    items: [item(0, "med-0010", "Amlodipine 5mg", "Viên", 30, 1, 0, 0, 0)],
  },
  {
    id: "tpl-0003",
    code: "TPL-DAU-DA-DAY",
    name: "Viêm dạ dày",
    diagnosis: "Viêm dạ dày",
    icd10Code: "K29",
    durationDays: 14,
    notes: "Tránh đồ cay nóng, rượu bia.",
    visible: true,
    active: true,
    createdAt: nowISO,
    updatedAt: nowISO,
    version: 1,
    items: [item(0, "med-0006", "Omeprazole 20mg", "Viên", 14, 1, 0, 0, 0)],
  },
  {
    id: "tpl-0004",
    code: "TPL-HEN-PQ",
    name: "Hen phế quản – cắt cơn",
    diagnosis: "Hen phế quản",
    icd10Code: "J45",
    durationDays: 7,
    notes: "Dùng khi khó thở.",
    visible: false,
    active: true,
    createdAt: nowISO,
    updatedAt: nowISO,
    version: 1,
    items: [item(0, "med-0009", "Ventolin xịt 100mcg", "Bình xịt", 1, 0, 0, 0, 0)],
  },
];

function toSummary(
  t: PrescriptionTemplateResponse
): PrescriptionTemplateSummaryResponse {
  return {
    id: t.id,
    code: t.code,
    name: t.name,
    diagnosis: t.diagnosis,
    icd10Code: t.icd10Code,
    durationDays: t.durationDays,
    itemCount: t.items.length,
    visible: t.visible,
    active: t.active,
    updatedAt: t.updatedAt,
  };
}

function find(id: string): PrescriptionTemplateResponse {
  const t = TEMPLATES.find((x) => x.id === id);
  if (!t) throw new Error(`Mock: mẫu đơn ${id} không tồn tại`);
  return t;
}

function fromSave(
  req: PrescriptionTemplateSaveRequest,
  id: string,
  code: string
): PrescriptionTemplateResponse {
  return {
    id,
    code,
    name: req.name,
    diagnosis: req.diagnosis ?? null,
    icd10Code: req.icd10Code ?? null,
    durationDays: req.durationDays,
    notes: req.notes ?? null,
    visible: req.visible ?? true,
    active: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    version: 1,
    items: req.items.map((it, i) => ({
      id: `pti-${id}-${i}`,
      medicineId: it.medicineId,
      medicineName: it.medicineId,
      medicineUnit: it.doseUnit ?? "Viên",
      medicineStrength: null,
      quantity: it.quantity,
      doseMorning: it.doseMorning ?? 0,
      doseNoon: it.doseNoon ?? 0,
      doseAfternoon: it.doseAfternoon ?? 0,
      doseEvening: it.doseEvening ?? 0,
      doseUnit: it.doseUnit ?? null,
      route: it.route ?? null,
      routeLabel: it.route === "PO" ? "Uống" : null,
      mealTiming: it.mealTiming ?? null,
      note: it.note ?? null,
      itemOrder: i,
    })),
  };
}

export const mockPrescriptionTemplates = {
  list(
    filter: PrescriptionTemplateListFilter | undefined,
    pageable: { page?: number; size?: number; sort?: string }
  ): Promise<PageResponse<PrescriptionTemplateSummaryResponse>> {
    let rows = TEMPLATES;
    if (filter?.q) {
      const n = filter.q.trim().toLowerCase();
      rows = rows.filter(
        (t) =>
          t.name.toLowerCase().includes(n) ||
          t.code.toLowerCase().includes(n) ||
          (t.diagnosis ?? "").toLowerCase().includes(n)
      );
    }
    if (filter?.icd10Code)
      rows = rows.filter((t) => t.icd10Code === filter.icd10Code);
    if (filter?.visibleOnly) rows = rows.filter((t) => t.visible);
    const page = pageable.page ?? 0;
    const size = pageable.size ?? 20;
    const sorted = rows
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, "vi"))
      .map(toSummary);
    return delay({
      content: sorted.slice(page * size, page * size + size),
      totalElements: sorted.length,
      totalPages: Math.max(1, Math.ceil(sorted.length / size)),
      number: page,
      size,
    });
  },

  get(id: string): Promise<PrescriptionTemplateResponse> {
    return delay({ ...find(id) });
  },

  create(
    req: PrescriptionTemplateSaveRequest
  ): Promise<PrescriptionTemplateResponse> {
    const t = fromSave(
      req,
      `tpl-${Math.random().toString(36).slice(2, 8)}`,
      req.code ?? `TPL-${Math.random().toString(36).slice(2, 6).toUpperCase()}`
    );
    TEMPLATES.unshift(t);
    return delay({ ...t });
  },

  update(
    id: string,
    req: PrescriptionTemplateSaveRequest
  ): Promise<PrescriptionTemplateResponse> {
    const t = find(id);
    const updated = fromSave(req, id, req.code ?? t.code);
    updated.createdAt = t.createdAt;
    updated.version = t.version + 1;
    Object.assign(t, updated);
    return delay({ ...t });
  },

  duplicate(
    id: string,
    newCode: string
  ): Promise<PrescriptionTemplateResponse> {
    const src = find(id);
    const copy: PrescriptionTemplateResponse = {
      ...src,
      id: `tpl-${Math.random().toString(36).slice(2, 8)}`,
      code: newCode,
      name: `${src.name} (sao chép)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1,
    };
    TEMPLATES.unshift(copy);
    return delay({ ...copy });
  },

  show(id: string): Promise<void> {
    find(id).visible = true;
    return delay(undefined);
  },
  hide(id: string): Promise<void> {
    find(id).visible = false;
    return delay(undefined);
  },
  softDelete(id: string): Promise<void> {
    const i = TEMPLATES.findIndex((x) => x.id === id);
    if (i >= 0) TEMPLATES.splice(i, 1);
    return delay(undefined);
  },
  restore(_id: string): Promise<void> {
    return delay(undefined);
  },
};
